import { hash } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { ensureSafeComponent } from '@embroider/util';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type {
  EmberTableColumn,
  EmberTableRow,
  EmberTableSort,
  TableRowMeta,
} from 'ember-table';
import type { EmberTbodyArgs } from 'ember-table/components/ember-tbody/component';
import type { EmberTheadArgs } from 'ember-table/components/ember-thead/component';
import { and, eq, not, or } from 'ember-truth-helpers';

import type { IconName } from '../../types/components/prism/icon.ts';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '../../types/components/prism/table.ts';
import PrismCheckbox from './checkbox.gts';
import PrismIcon from './icon.gts';
import PrismTableCustomEmberTable from './table/custom-ember-table.gts';
import PrismTableHeadersThSortWrapper from './table/headers/th-sort-wrapper.gts';
import PrismTableLoadingBody from './table/loading-body.gts';
import PrismTableRow from './table/row.gts';
import PrismTooltip from './tooltip.gts';

export function getNumberOfLeafRows(rows: CustomEmberTableRow[]): number {
  return rows.reduce((acc, row) => {
    if (
      !row['children'] ||
      !Array.isArray(row['children']) ||
      row['children'].length === 0
    ) {
      return acc + 1;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return acc + getNumberOfLeafRows(row['children']);
  }, 0);
}

export interface PrismTableSignature {
  Args: {
    checkboxSelectionMode?: EmberTbodyArgs<CustomEmberTableRow>['checkboxSelectionMode'];
    columns: CustomEmberTableColumn[];
    /**
     * Ember table width adjustment. See https://opensource.addepar.com/ember-table/docs/guides/header/size-constraints
     */
    containerWidthAdjustment?: number;
    disableCheckboxSelection?: boolean;
    disabledCheckboxIcon?: IconName;
    disabledCheckboxTooltipText?: string;
    enableAutoSortsUpdate?: boolean;
    estimateRowHeight?: number;
    fillColumnIndex?: EmberTheadArgs<
      EmberTableRow,
      EmberTableColumn
    >['fillColumnIndex'];
    fillMode?: EmberTheadArgs<EmberTableRow, EmberTableColumn>['fillMode'];
    footerRows?: CustomEmberTableRow[];
    highlightTitleColumn?: boolean;
    /**
     * The `initialSorts` argument is used to control the sort order of the table.
     * If `enableAutoSortsUpdate` is set to true, this value will be used as the initial sort order of the table.
     */
    initialSorts?: EmberTableSort[];
    isLoading?: boolean;
    isLoadingMore?: boolean;
    /**
     * An action that is triggered when the table reaches the last row.
     */
    lastReached?: () => void;
    nrOfEmptyRows?: number;
    nrOfLoadingRows?: number;
    onRowClick?: (event: {
      event: Event;
      rowMeta: TableRowMeta;
      rowValue: CustomEmberTableRow;
    }) => unknown;
    onRowCollapseToggle?: (row: CustomEmberTableRow) => void | undefined;
    onRowHover?: (event: {
      event: Event;
      rowMeta: TableRowMeta | null;
      rowValue: CustomEmberTableRow | null;
    }) => unknown;
    onRowSelect?: EmberTbodyArgs<CustomEmberTableRow>['onSelect'];
    onTableSort?: (sorts: EmberTableSort[]) => unknown;
    onUpdateSorts?: (sorts: EmberTableSort[]) => unknown;
    /**
     * This is used to determine whether the bulk edit row should have one cell (<th>) that spans the full width of the table.
     * If set to false, provide <th> elements inside the block and customise the colspan.
     * @default true
     */
    oneCellBulkEditRow?: boolean;
    overlayMessage?: string;
    removeTableBorder?: boolean;
    /** A flag that tells the table to render all of its rows at once. */
    renderAll?: boolean;
    rowSelection?: EmberTbodyArgs<CustomEmberTableRow>['selection'];
    rowSelectionMode?: EmberTbodyArgs<CustomEmberTableRow>['rowSelectionMode'];
    rows: CustomEmberTableRow[];
    sortEmptyLast?: boolean;
    sortFunction?: <T = string>(
      itemA: T,
      itemB: T,
      sorts: EmberTableSort[],
      compare: (valueA: T, valueB: T, sortEmptyLast: boolean) => number,
      sortEmptyLast: boolean,
    ) => number;
    /**
     * The `sorts` argument is used to control the sort order of the table.
     * If `enableAutoSortsUpdate` is set to true, this value will be ignored.
     */
    sorts?: EmberTableSort[];
    widthConstraint?: EmberTheadArgs<
      EmberTableRow,
      EmberTableColumn
    >['widthConstraint'];
  };
  Blocks: {
    bulkEditRow?: [{ numberOfSelectedRows: number }];
    emptyState?: [];
  };
  Element: HTMLDivElement;
}

export default class PrismTable extends Component<PrismTableSignature> {
  @tracked sorts: EmberTableSort[] = [];

  /**
   * We are not using <EmberTable> directly in the template because we need to specify our custom column type.
   * If not, we get typing errors for the custom args.
   */
  emberTableComponent = PrismTableCustomEmberTable;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get areAllRowsSelected() {
    const { rowSelection } = this.args;

    const selectableRows = this.args.rows.filter((row) =>
      this.filterSelectableRow(row),
    );

    return (
      Array.isArray(rowSelection) &&
      selectableRows.every((row) => rowSelection.includes(row))
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get areSomeRowsSelected() {
    return (
      Array.isArray(this.args.rowSelection) && this.args.rowSelection.length > 0
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get checkboxSelectionMode() {
    return this.args.checkboxSelectionMode ?? 'none';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columnCount() {
    const countLeafColumns = (columns: EmberTableColumn[]): number => {
      return columns.reduce((count, column) => {
        if (
          column.subcolumns &&
          Array.isArray(column.subcolumns) &&
          column.subcolumns.length > 0
        ) {
          return count + countLeafColumns(column.subcolumns);
        }

        return count + 1;
      }, 0);
    };

    return countLeafColumns(this.args.columns);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get disableSelectAllCheckboxSelection() {
    const hasRowsWithCheckboxSelectionEnabled = this.args.rows.some((row) =>
      this.filterSelectableRow(row),
    );

    return !hasRowsWithCheckboxSelectionEnabled;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get estimateRowHeight() {
    return this.args.estimateRowHeight ?? 36;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasRowsThatCanCollapse() {
    return this.args.rows.some(
      (row) =>
        'children' in row &&
        Array.isArray(row['children']) &&
        row['children'].length > 0,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get highlightTitleColumn() {
    return this.args.highlightTitleColumn ?? true;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberOfSelectedRows() {
    if (!Array.isArray(this.args.rowSelection)) {
      return 0;
    }

    return getNumberOfLeafRows(this.args.rowSelection);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get oneCellBulkEditRow() {
    return this.args.oneCellBulkEditRow ?? true;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get renderAll() {
    return this.args.renderAll ?? true;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rowSelectionMode() {
    return this.args.rowSelectionMode ?? 'none';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showSelectAllCheckbox() {
    return this.checkboxSelectionMode === 'multiple';
  }

  constructor(owner: Owner, args: PrismTableSignature['Args']) {
    super(owner, args);

    /**
     * If `enableAutoSortsUpdate` is set to true, and:
     * - `initialSorts` arg is provided, it's value will be used as the initial sort order of the table.
     * - `initialSorts` arg is not provided, the table will default to sorting by the first column in descending order.
     */
    if (this.args.enableAutoSortsUpdate && this.args.initialSorts) {
      this.sorts = this.args.initialSorts;
    } else {
      this.sorts = [
        {
          valuePath: this.args.columns[0]?.valuePath ?? '',
          isAscending: false,
        },
      ];
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  filterSelectableRow(row: CustomEmberTableRow) {
    if (this.args.disableCheckboxSelection) {
      return row.disableCheckboxSelection === false;
    }

    return !row.disableCheckboxSelection;
  }

  @action
  handleSelectAll(): void {
    const { onRowSelect, rows } = this.args;

    if (this.areAllRowsSelected) {
      onRowSelect?.([]);
    } else {
      const selectableRows = rows.filter((row) =>
        this.filterSelectableRow(row),
      );

      onRowSelect?.(selectableRows);
    }
  }

  /**
   * An action that gets called when the user interacts with the sorting feature of the table.
   * Ember Table calls this action with the new sort order as an argument.
   * This action is responsible for updating our local `sorts` state and calling the `onTableSort` action to notify the parent component.
   */
  @action
  onUpdateSorts(sorts: EmberTableSort[]): void {
    if (sorts.length > 1) {
      // multi-column sort, default behavior
      this.sorts = sorts;
      return;
    }

    const hasExistingSort = this.sorts.length !== 0;
    const isDefaultSort = sorts.length === 0;

    if (hasExistingSort && isDefaultSort) {
      /**
       * Ember Table has three sort states: Ascending, Descending, and Unsorted.
       * However, we want to limit the sort states to only two: Ascending and Descending.
       * When the state is Unsorted, an empty sorts array is passed.
       * To achieve the two sort states, we override the empty sorts array with the reversed previous sort.
       */
      const newSorts = [
        {
          valuePath: this.sorts[0]?.valuePath ?? '',
          isAscending: !this.sorts[0]?.isAscending,
        },
      ];
      this.sorts = newSorts;
      this.args.onTableSort?.(this.sorts);
      return;
    }

    this.sorts = sorts;
    this.args.onTableSort?.(this.sorts);
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="prism-table
        {{if @isLoading 'loading'}}
        {{if
          (and (has-block 'emptyState') (eq @rows.length 0))
          'prism-table--empty-state-visible'
        }}
        {{if this.highlightTitleColumn 'prism-table--highlight-title-column'}}"
    >
      <this.emberTableComponent
        aria-busy={{or @isLoading @isLoadingMore}}
        aria-live={{if (or @isLoading @isLoadingMore) "polite" "off"}}
        class={{unless @removeTableBorder "table-border"}}
        data-test-id="table"
        ...attributes
        as |tableComponent|
      >
        <tableComponent.head
          @columns={{@columns}}
          @containerWidthAdjustment={{or @containerWidthAdjustment -15}}
          @enableReorder={{false}}
          @fillColumnIndex={{@fillColumnIndex}}
          @fillMode={{@fillMode}}
          @onUpdateSorts={{if
            @enableAutoSortsUpdate
            this.onUpdateSorts
            @onUpdateSorts
          }}
          @resizeMode="fluid"
          @sortEmptyLast={{@sortEmptyLast}}
          @sortFunction={{@sortFunction}}
          @sorts={{if @enableAutoSortsUpdate this.sorts @sorts}}
          @widthConstraint={{@widthConstraint}}
          class="table-header"
          data-test-id="table-header"
          as |header|
        >
          <header.row
            class="table-header-row table-row"
            data-test-id="table-header-row"
            as |headerRow|
          >
            <headerRow.cell
              class="table-header-cell text-200--bold
                {{if
                  headerRow.columnValue.thClassNames
                  headerRow.columnValue.thClassNames
                }}
                {{if headerRow.columnValue.hasRightBorder 'right-border'}}
                "
              data-test-id="table-header-cell"
              as |column columnMeta|
            >
              <div class="prism-table-header-cell__content">
                {{#if (and (eq columnMeta.index 0) this.showSelectAllCheckbox)}}
                  <div class="prism-table-checkbox-spacer">
                    {{! Wrap in a <div> so the tooltip isn't rendered on the parent div, because it has padding. }}
                    <div data-test-id="tooltip-container">
                      {{#if
                        (and
                          this.disableSelectAllCheckboxSelection
                          @disabledCheckboxIcon
                        )
                      }}
                        <PrismIcon
                          {{! @glint-expect-error: Incorrect type }}
                          @name={{@disabledCheckboxIcon}}
                          @size="small"
                          data-test-id="table-checkbox-icon-select-all"
                        />
                      {{else}}
                        <PrismCheckbox
                          @checked={{and
                            (not this.disableSelectAllCheckboxSelection)
                            this.areAllRowsSelected
                          }}
                          @disabled={{this.disableSelectAllCheckboxSelection}}
                          @indeterminate={{this.areSomeRowsSelected}}
                          @onChange={{this.handleSelectAll}}
                          aria-label="Select all"
                          data-test-id="table-checkbox-select-all"
                        />
                      {{/if}}

                      {{#if
                        (and
                          this.disableSelectAllCheckboxSelection
                          @disabledCheckboxTooltipText
                        )
                      }}
                        {{! @glint-expect-error: Incorrect type }}
                        <PrismTooltip @text={{@disabledCheckboxTooltipText}} />
                      {{/if}}
                    </div>
                  </div>
                {{/if}}
                <div class="prism-table-header-sort-content">
                  <PrismTableHeadersThSortWrapper
                    @column={{column}}
                    @columnMeta={{columnMeta}}
                  >
                    {{#if column.thComponent}}
                      {{#let
                        (component
                          (ensureSafeComponent column.thComponent this)
                        )
                        as |ThComponent|
                      }}
                        <ThComponent
                          @column={{column}}
                          @columnMeta={{columnMeta}}
                        />
                      {{/let}}
                    {{else}}
                      {{column.name}}
                    {{/if}}
                  </PrismTableHeadersThSortWrapper>
                </div>
              </div>
            </headerRow.cell>
          </header.row>

          {{#if (and (has-block "bulkEditRow") this.areSomeRowsSelected)}}
            <tr data-test-id="table-bulk-edit-row">
              {{#if this.oneCellBulkEditRow}}
                <th colspan={{@columns.length}}>
                  <div
                    class="table-header-bulk-edit-cell-content text-200--regular"
                  >
                    {{yield
                      (hash numberOfSelectedRows=this.numberOfSelectedRows)
                      to="bulkEditRow"
                    }}
                  </div>
                </th>
              {{else}}
                {{yield
                  (hash numberOfSelectedRows=this.numberOfSelectedRows)
                  to="bulkEditRow"
                }}
              {{/if}}
            </tr>
          {{/if}}
        </tableComponent.head>

        {{#if @isLoading}}
          <PrismTableLoadingBody
            @columnCount={{this.columnCount}}
            @nrOfEmptyRows={{or @nrOfEmptyRows 18}}
            @nrOfLoadingRows={{or @nrOfLoadingRows 2}}
          />
        {{else if (and (has-block "emptyState") (eq @rows.length 0))}}
          <tbody class="table-body">
            <tr>
              <td colspan={{@columns.length}}>
                {{yield to="emptyState"}}
              </td>
            </tr>
          </tbody>
        {{else}}
          <tableComponent.body
            @checkboxSelectionMode={{this.checkboxSelectionMode}}
            @estimateRowHeight={{this.estimateRowHeight}}
            @lastReached={{@lastReached}}
            @onSelect={{@onRowSelect}}
            @renderAll={{this.renderAll}}
            @rowSelectionMode={{this.rowSelectionMode}}
            @rows={{@rows}}
            @selection={{@rowSelection}}
            class="table-body text-200--regular
              {{if
                this.hasRowsThatCanCollapse
                'table-body--with-collapsible-rows'
              }}
              "
            data-test-id="table-body"
            as |body|
          >
            <PrismTableRow
              @body={{body}}
              @checkboxSelectionMode={{this.checkboxSelectionMode}}
              @disableCheckboxSelection={{@disableCheckboxSelection}}
              @disabledCheckboxIcon={{@disabledCheckboxIcon}}
              @disabledCheckboxTooltipText={{@disabledCheckboxTooltipText}}
              @onRowClick={{@onRowClick}}
              @onRowCollapseToggle={{@onRowCollapseToggle}}
              @onRowHover={{@onRowHover}}
            />
          </tableComponent.body>

          {{#if @isLoadingMore}}
            <PrismTableLoadingBody
              @columnCount={{this.columnCount}}
              @nrOfEmptyRows={{or @nrOfEmptyRows 18}}
              @nrOfLoadingRows={{or @nrOfLoadingRows 2}}
            />
          {{/if}}

          {{#if @footerRows}}
            <tableComponent.foot
              @rows={{@footerRows}}
              class="table-footer text-200--regular"
              data-test-id="table-footer"
              as |foot|
            >
              <PrismTableRow
                @body={{foot}}
                @checkboxSelectionMode={{this.checkboxSelectionMode}}
                @isFooterRow={{true}}
              />
            </tableComponent.foot>
          {{/if}}
        {{/if}}

      </this.emberTableComponent>

      {{#if @overlayMessage}}
        <div class="message-overlay">
          <div class="text-500--regular">
            {{@overlayMessage}}
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}
